import { useT } from "@transifex/react";
import { memo, useCallback, useState } from "react";
import FilterBarLayout from "../../../basic/filterBarLayout/FilterBarLayout";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import SearchableFilter, { SEARCHABLE_FILTER_ALL_VALUE } from "../../../basic/searchableFilter/SearchableFilter";
import SortSelect from "../../../basic/sortSelect/SortSelect";
import { ClassActivityCategory } from "../../../constants/ClassActivityConstants";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";

export enum ClassDetailsSortByOptions {
    CREATED_DESC = "CREATED_DESC",
    CREATED_ASC = "CREATED_ASC",
}

export enum ActivityVisibilityFilter {
    ALL = "",
    SHARED = "SHARED",
    NOT_SHARED = "NOT_SHARED",
}

interface ClassDetailsFilterBarProps {
    activityFilter: string;
    onActivityFilterChange: (value: string) => void;
    visibilityFilter: string;
    onVisibilityFilterChange: (value: string) => void;
    sortBy: ClassDetailsSortByOptions;
    onSortByChange: (value: ClassDetailsSortByOptions) => void;
}

export const ClassDetailsFilterBar = memo((props: ClassDetailsFilterBarProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const [isExpanded, setIsExpanded] = useState(false);

    const t_filter = t("Filter", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_all = t("All", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_activityType = t("Activity type", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_test = t("Test", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_vocabTraining = t("Vocabulary training", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_visibility = t("Visibility", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_shared = t("Shared", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_notShared = t("Not shared", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_createdDesc = t("Creation date (descending)", { _tags: "SchoolClasses, ClassDetailsFilterBar" });
    const t_createdAsc = t("Creation date (ascending)", { _tags: "SchoolClasses, ClassDetailsFilterBar" });

    const activityOptions = [
        { name: t_all, value: SEARCHABLE_FILTER_ALL_VALUE },
        { name: t_test, value: ClassActivityCategory.TEST },
        { name: t_vocabTraining, value: ClassActivityCategory.VOCAB_TRAINING },
    ];

    const visibilityOptions = [
        { name: t_all, value: SEARCHABLE_FILTER_ALL_VALUE },
        { name: t_shared, value: ActivityVisibilityFilter.SHARED },
        { name: t_notShared, value: ActivityVisibilityFilter.NOT_SHARED },
    ];

    const sortOptions = [
        { name: t_createdDesc, value: ClassDetailsSortByOptions.CREATED_DESC },
        { name: t_createdAsc, value: ClassDetailsSortByOptions.CREATED_ASC },
    ];

    const onSortByChange = props.onSortByChange;
    const handleSortByChange = useCallback(
        (val: string) => onSortByChange(val as ClassDetailsSortByOptions),
        [onSortByChange]
    );

    return (
        <div className="flex flex-col gap-s w-full">
            <button
                type="button"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="flex items-center gap-xxs bg-transparent border-none cursor-pointer p-no w-fit"
            >
                <PhaseSixIcon
                    name="sliders"
                    className="text-xxl text-p6-grey-dark"
                />
                <span className="text-l text-p6-grey-dark">{t_filter}</span>
                <PhaseSixIcon
                    name={isExpanded ? "chevron-up" : "chevron-down"}
                    className="text-xl text-p6-grey-dark"
                />
            </button>

            {isExpanded && (
                <FilterBarLayout
                    filterContent={(filterLabelId) => (
                        <>
                            <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "min-w-[200px]", "w-full")}>
                                <SearchableFilter
                                    options={activityOptions}
                                    value={props.activityFilter}
                                    onChange={props.onActivityFilterChange}
                                    placeholder={t_activityType}
                                    aria-labelledby={filterLabelId}
                                />
                            </div>

                            <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "min-w-[200px]", "w-full")}>
                                <SearchableFilter
                                    options={visibilityOptions}
                                    value={props.visibilityFilter}
                                    onChange={props.onVisibilityFilterChange}
                                    placeholder={t_visibility}
                                    aria-labelledby={filterLabelId}
                                />
                            </div>
                        </>
                    )}
                    sortContent={(sortLabelId) => (
                        <SortSelect
                            options={sortOptions}
                            value={props.sortBy}
                            onChange={handleSortByChange}
                            aria-labelledby={sortLabelId}
                        />
                    )}
                />
            )}
        </div>
    );
});
